import { Component, computed, input } from '@angular/core';
import { IntradayChartPoint } from '../../../../models/intraday-chart.model';
import { prepareTrades } from '../../../../models/intraday-chart.utils';
import { buildSparklinePaths, mapTradesToPoints } from './sparkline.utils';

/** Figma Chart Stock Card inner size (68×26) */
const VIEW_WIDTH = 68;
const VIEW_HEIGHT = 26;

const POSITIVE_STROKE = 'var(--color-green-500)';
const NEGATIVE_STROKE = 'var(--color-red-500)';
const NEUTRAL_STROKE = 'var(--color-gray-500)';
const BASELINE_STROKE = 'var(--color-gray-300)';
const POSITIVE_FILL = 'var(--color-green-100)';
const NEGATIVE_FILL = 'var(--color-red-100)';

@Component({
	selector: 'app-symbol-sparkline',
	host: {
		class: 'block shrink-0',
		role: 'img',
		'aria-hidden': 'true'
	},
	template: `
		@if (hasPoints()) {
			<svg
				class="overflow-visible"
				[attr.width]="VIEW_WIDTH"
				[attr.height]="VIEW_HEIGHT"
				[attr.viewBox]="'0 0 ' + VIEW_WIDTH + ' ' + VIEW_HEIGHT">
				<line
					x1="0"
					stroke-width=".5"
					stroke-dasharray="2 2"
					[attr.y1]="paths().baselineY"
					[attr.x2]="VIEW_WIDTH"
					[attr.y2]="paths().baselineY"
					[attr.stroke]="BASELINE_STROKE" />
				@if (paths().positiveFill) {
					<path fill-opacity="0.55" [attr.d]="paths().positiveFill" [attr.fill]="POSITIVE_FILL" />
				}
				@if (paths().negativeFill) {
					<path fill-opacity="0.55" [attr.d]="paths().negativeFill" [attr.fill]="NEGATIVE_FILL" />
				}
				@if (paths().positiveStroke) {
					<path
						fill="none"
						stroke-width=".75"
						stroke-linecap="round"
						stroke-linejoin="round"
						[attr.d]="paths().positiveStroke"
						[attr.stroke]="POSITIVE_STROKE" />
				}
				@if (paths().negativeStroke) {
					<path
						fill="none"
						stroke-width=".75"
						stroke-linecap="round"
						stroke-linejoin="round"
						[attr.d]="paths().negativeStroke"
						[attr.stroke]="NEGATIVE_STROKE" />
				}
				@if (paths().neutralStroke) {
					<path
						fill="none"
						stroke-width=".75"
						stroke-linecap="round"
						stroke-linejoin="round"
						[attr.d]="paths().neutralStroke"
						[attr.stroke]="NEUTRAL_STROKE" />
				}
			</svg>
		} @else {
			<div
				class="flex h-[26px] w-[68px] items-center justify-center font-bold text-2 leading-none text-gray-500"
				data-testid="sparkline-empty-state">
				داده‌ای یافت نشد
			</div>
		}
	`
})
export class SymbolSparklineComponent {
	protected readonly VIEW_WIDTH = VIEW_WIDTH;
	protected readonly VIEW_HEIGHT = VIEW_HEIGHT;
	protected readonly POSITIVE_STROKE = POSITIVE_STROKE;
	protected readonly NEGATIVE_STROKE = NEGATIVE_STROKE;
	protected readonly NEUTRAL_STROKE = NEUTRAL_STROKE;
	protected readonly BASELINE_STROKE = BASELINE_STROKE;
	protected readonly POSITIVE_FILL = POSITIVE_FILL;
	protected readonly NEGATIVE_FILL = NEGATIVE_FILL;

	/** Trades for the current session (time + price). Invalid / zero prices are ignored. */
	readonly trades = input<IntradayChartPoint[]>([]);
	/** Yesterday's closing price — used as the baseline for color and fill. */
	readonly baseline = input.required<number>();
	/** Current time (ms). Shared X-axis end for all symbols until market close. */
	readonly now = input(Date.now());

	private readonly validTrades = computed(() => prepareTrades(this.trades()));

	readonly hasPoints = computed(() => this.validTrades().length > 0);

	readonly paths = computed(() => {
		const trades = this.validTrades();
		const baseline = this.baseline();
		if (!trades.length || !Number.isFinite(baseline)) {
			return {
				positiveFill: '',
				negativeFill: '',
				positiveStroke: '',
				negativeStroke: '',
				neutralStroke: '',
				baselineY: VIEW_HEIGHT / 2
			};
		}

		const { points, baselineY } = mapTradesToPoints(trades, baseline, VIEW_WIDTH, VIEW_HEIGHT, this.now());

		return buildSparklinePaths(points, baseline, baselineY);
	});
}
